import { useEffect, useState, useCallback } from 'react';
import { useGame } from '../hooks/useGame';
import { useIsMobile } from '../hooks/useIsMobile';
import { Keypad } from './ui/Keypad';

const styles = {
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        color: 'white',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        flexShrink: 0,
    },
    statGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    statIcon: {
        padding: '6px',
        borderRadius: '8px',
        fontSize: '18px',
    },
    statIconScore: {
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
    },
    statIconTime: {
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
    },
    statContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    statContentRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    statLabel: {
        fontSize: '10px',
        color: '#94a3b8',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: '1px',
    },
    statValue: {
        fontSize: '18px',
        fontFamily: 'monospace',
        fontWeight: 700,
    },
    progressBar: {
        width: '100%',
        height: '3px',
        backgroundColor: '#334155',
        flexShrink: 0,
    },
    progressFill: {
        height: '100%',
        transition: 'width 0.1s linear',
    },
    gameArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        gap: '20px',
        minHeight: 0,
    },
    problemContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
    },
    solveLabel: {
        color: '#94a3b8',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '3px',
        fontWeight: 700,
    },
    problem: {
        fontSize: '52px',
        fontWeight: 900,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        transition: 'all 0.3s ease',
    },
    operator: {
        color: '#64748b',
    },
    inputDisplay: {
        height: '60px',
        width: '100%',
        maxWidth: '240px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '14px',
        border: '2px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s ease',
    },
    inputText: {
        fontSize: '28px',
        fontWeight: 700,
        letterSpacing: '4px',
    },
    inputPlaceholder: {
        color: 'rgba(255, 255, 255, 0.1)',
        fontSize: '28px',
        fontWeight: 700,
    },
    desktopHint: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#64748b',
    },
    hintBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    hintText: {
        fontSize: '12px',
        fontWeight: 500,
    },
    keypadContainer: {
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        flexShrink: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 20px)',
    },
    spacer: {
        height: '40px',
        width: '100%',
        flexShrink: 0,
    },
};

export const Game = ({ onGameOver }) => {
    const { score, timeLeft, currentProblem, feedback, startGame, submitAnswer } = useGame(onGameOver);
    const [inputValue, setInputValue] = useState('');
    const isMobile = useIsMobile();

    useEffect(() => {
        startGame();
    }, [startGame]);

    const handlePress = useCallback((digit) => {
        setInputValue((prev) => {
            if (prev.length < 6) return prev + digit;
            return prev;
        });
    }, []);

    const handleDelete = useCallback(() => {
        setInputValue((prev) => prev.slice(0, -1));
    }, []);

    const handleSubmit = useCallback(() => {
        setInputValue((currentValue) => {
            if (!currentValue) return currentValue;
            submitAnswer(parseInt(currentValue, 10));
            return '';
        });
    }, [submitAnswer]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            if (e.key >= '0' && e.key <= '9') {
                handlePress(e.key);
            } else if (e.key === 'Backspace') {
                handleDelete();
            } else if (e.key === 'Enter') {
                handleSubmit();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handlePress, handleDelete, handleSubmit]);

    const getFeedbackStyle = () => {
        if (feedback === 'correct') return { color: '#34d399', transform: 'scale(1.1)' };
        if (feedback === 'wrong') return { color: '#f87171' };
        return { color: 'white' };
    };

    const getInputStyle = () => {
        if (feedback === 'wrong') {
            return {
                ...styles.inputDisplay,
                borderColor: 'rgba(239, 68, 68, 0.5)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
            };
        }
        return styles.inputDisplay;
    };

    const timeColor = timeLeft < 5 ? '#f87171' : 'white';
    const progressColor = timeLeft < 5 ? '#ef4444' : '#a855f7';
    const progressWidth = `${Math.min(100, (timeLeft / 10) * 100)}%`;

    return (
        <div style={styles.container}>
            {/* Header Stats */}
            <div style={styles.header}>
                <div style={styles.statGroup}>
                    <div style={{ ...styles.statIcon, ...styles.statIconScore }}>🏆</div>
                    <div style={styles.statContent}>
                        <span style={styles.statLabel}>Score</span>
                        <span style={styles.statValue}>{score}</span>
                    </div>
                </div>

                <div style={styles.statGroup}>
                    <div style={{ ...styles.statContentRight, color: timeColor }}>
                        <span style={styles.statLabel}>Time</span>
                        <span style={styles.statValue}>{timeLeft.toFixed(1)}s</span>
                    </div>
                    <div style={{ ...styles.statIcon, ...styles.statIconTime }}>⏱️</div>
                </div>
            </div>

            {/* Progress Bar */}
            <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: progressWidth, backgroundColor: progressColor }} />
            </div>

            {/* Main Game Area */}
            <div style={styles.gameArea}>
                <div style={styles.problemContainer}>
                    <span style={styles.solveLabel}>Solve</span>
                    <div style={{ ...styles.problem, ...getFeedbackStyle() }}>
                        <span>{currentProblem.n1}</span>
                        <span style={styles.operator}>+</span>
                        <span>{currentProblem.n2}</span>
                    </div>
                </div>

                {/* Input Display */}
                <div style={getInputStyle()}>
                    {inputValue ? (
                        <span style={styles.inputText}>{inputValue}</span>
                    ) : (
                        <span style={styles.inputPlaceholder}>?</span>
                    )}
                </div>

                {/* Desktop Hint */}
                {!isMobile && (
                    <div style={styles.desktopHint}>
                        <div style={styles.hintBox}>
                            <span>⌨️</span>
                            <span style={styles.hintText}>Type Answer</span>
                        </div>
                        <div style={styles.hintBox}>
                            <span>↵</span>
                            <span style={styles.hintText}>Enter</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Controls */}
            {isMobile ? (
                <div style={styles.keypadContainer}>
                    <Keypad 
                        onPress={handlePress} 
                        onDelete={handleDelete} 
                        onSubmit={handleSubmit} 
                    />
                </div>
            ) : (
                <div style={styles.spacer}></div>
            )}
        </div>
    );
};
